<script lang="ts">
  import { goto } from '$app/navigation';

  import { createEventDispatcher } from 'svelte';
  import { inview } from 'svelte-inview';

  import type { CommonDto } from '@do/data-transfer-objects';
  import { InstitutionDomain } from '@do/domain';
  import { DirectusImage, Preset } from '@do/svelte-directus-images';

  import { getInstitutionTypeContext } from '@shared/configs';
  import { pointer } from '@shared/libs/tools';
  import { cn, is } from '@shared/utils';

  import { searchLibs } from '@entities/search';

  import { imageIdToNumber } from '../libs';

  export let image: CommonDto.Photo | null;
  export let name: string;
  export let artist: string | null;
  export let year: string | null | undefined;
  export let slug: string;
  export let isImageBlurred = false;

  export let hasHover = true;

  const institutionType = getInstitutionTypeContext();

  const dispatcher = createEventDispatcher<{ navigate: void }>();
  const options = { rootMargin: '0px', threshold: 0.1 };
  const appearanceDelay = imageIdToNumber(image?.id);

  let wasInview = false;
  let isImageLoaded = false;
  let imgElement: HTMLImageElement;

  $: isVisible = (image ? isImageLoaded : true) && wasInview;
  $: opacityTransitionClasses = `transition-opacity ${is(isVisible, 'opacity-1', 'opacity-0')} duration-500`;

  function handleEnter() {
    wasInview = true;
  }

  function handleImageLoad() {
    isImageLoaded = true;
  }
</script>

<a
  href="/{slug}/"
  on:click="{() => {
    goto(`/${slug}/`);
    dispatcher('navigate');
  }}"
  use:inview="{options}"
  on:inview_enter="{handleEnter}"
>
  <div
    class="group relative flex w-full break-inside-avoid items-center gap-5 transition-transform duration-500 {is(
      hasHover,
      'hover:scale-[102%]',
    )}"
  >
    <div
      class="{is(
        isVisible,
        'opacity-1 translate-y-0 scale-100',
        'translate-y-[80%] scale-75 opacity-0',
      )} mb-2 overflow-hidden [transition:opacity_0.5s,transform_0.8s]
  "
      style="transition-delay: {appearanceDelay}ms;"
    >
      <div
        class="{cn('transition:transform_0.8s relative w-full cursor-pointer rounded-lg', {
          'translate-y-0': isVisible,
          '-translate-y-[60%]': !isVisible,
          'blur-xl': isImageBlurred,
        })}"
        use:pointer="{'view'}"
        style="transition-delay: {appearanceDelay}ms;"
      >
        <DirectusImage
          imageData="{image}"
          alt="{name}"
          imgClasses="w-20 h-20 max-w-fit max-h-full m-auto object-contain aspect-square"
          attributes="{{
            draggable: false,
          }}"
          pictureSizes="{{
            default: '80px',
          }}"
          presetKeys="{Preset.Thumbnail}"
          on:load="{handleImageLoad}"
          bind:imgElement
        />
      </div>
    </div>

    <div>
      <p
        class="underline-hover mb-1 {opacityTransitionClasses}"
        style="transition-delay: {searchLibs.getTransitionDelay(1, appearanceDelay)}"
      >
        {name}{#if year}, {year}{/if}
      </p>

      {#if artist && !InstitutionDomain.rules.isArtistLike(institutionType)}
        <p
          class="text-default-500 dark:text-default-50/60 {opacityTransitionClasses}"
          style="transition-delay: {searchLibs.getTransitionDelay(2, appearanceDelay)}"
        >
          {artist}
        </p>
      {/if}
    </div>
  </div>
</a>
